import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { LoadMore } from "@/components/common/LoadMore";
import { SaveCollectionControls, SaveCollectionEmptyState, SaveCollectionGrid, SaveCollectionPage, SaveCollectionSidebar, SavePersonCollectionCard } from "@/components/save/SaveCollectionTemplate";
import { useGlobalCompanySearch } from "@/lib/use-global-company-search";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { userErrorMessage } from "@/lib/user-error";

export function useCommunityCompanies(controls: ReactNode, enabled: boolean) {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  // No sort pill: the search ranks by relevance, and without a term genesis already returns the
  // directory by name (label ascending). A browser sort over the loaded pages would undo both (spark#1008).
  const companies = useGlobalCompanySearch({ query: search, pageSize: 30, skip: !enabled });
  const nodes = companies.nodes;
  useAssistantContext(enabled ? [{
    description: "the user's Community company directory",
    value: `${nodes.length} companies shown${search ? `; search "${search}"` : ""}`,
  }] : null);

  return <>
  <SaveCollectionPage
    title={intl.formatMessage({ id: "save.community.title", defaultMessage: "Community" })}
    description={<FormattedMessage id="save.community.companiesDescription" defaultMessage="Discover companies and open their profiles." />}
    showBackButton
    sidebarControls={controls}
    sidebar={<SaveCollectionSidebar
      embedded
      groups={[]}
      activeGroup="all"
      onGroupChange={() => setSearch("")}
    />}
    dataGraphql="GlobalSearch,CompanyIdentities"
  >
    <SaveCollectionControls
      statusLabel={intl.formatMessage({ id: "save.community.companyCount", defaultMessage: "{count, plural, one {# company} other {# companies}}" }, { count: nodes.length })}
      loading={companies.loading}
      errorMessage={companies.error ? userErrorMessage(intl, companies.error) : undefined}
      search={search}
      onSearchChange={setSearch}
      showUngroupedToggle={false}
      ungroupedLabel=""
      ungroupedActive={false}
      onUngroupedToggle={() => {}}
    />
    {!companies.loading && !companies.error && nodes.length === 0 ? <SaveCollectionEmptyState>
      <FormattedMessage id="save.community.companiesEmpty" defaultMessage="No companies found." />
    </SaveCollectionEmptyState> : <SaveCollectionGrid>
      {nodes.map((company) => <SavePersonCollectionCard
        key={company.id}
        id={`community-company-${company.id}`}
        title={company.name}
        openHref={`/c/${company.id}`}
        avatarContent={company.logo?.content}
        avatarMime={company.logo?.mimeType}
      />)}
    </SaveCollectionGrid>}
    <LoadMore hasMore={companies.hasMore} loading={companies.loadingMore} paused={companies.loadMoreFailed} onLoadMore={companies.loadMore} />
  </SaveCollectionPage>
  </>;
}
